import {
  Modal,
  TextInput,
  Box,
  Text,
  Group,
  ScrollArea,
  Kbd,
  useMantineTheme,
  ActionIcon,
} from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { useState, useEffect, useCallback, forwardRef, useMemo } from 'react';
import { IconSearch, IconCommand, IconCornerDownLeft } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface CommandAction {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string[];
  group?: string;
  keywords?: string[];
  onTrigger: () => void;
  disabled?: boolean;
}

export interface CommandPaletteProps {
  actions: CommandAction[];
  opened: boolean;
  onClose: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  shortcut?: string;
  maxHeight?: number | string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  recentActions?: string[];
  onRecentAction?: (id: string) => void;
  showShortcuts?: boolean;
}

const CommandPalette = forwardRef<HTMLDivElement, CommandPaletteProps>(
  (
    {
      actions,
      opened,
      onClose,
      placeholder = 'Type a command or search...',
      searchPlaceholder,
      emptyMessage = 'No results found',
      shortcut = 'mod+K',
      maxHeight = 400,
      gradientVariant = 'primary',
      recentActions = [],
      onRecentAction,
      showShortcuts = true,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Filter actions based on query
    const filteredActions = useMemo(() => {
      if (!query.trim()) {
        // Show recent actions first, then all actions
        const recent = recentActions
          .map((id) => actions.find((a) => a.id === id))
          .filter(Boolean) as CommandAction[];

        const rest = actions.filter((a) => !recentActions.includes(a.id));
        return [...recent, ...rest].filter((a) => !a.disabled);
      }

      const lowerQuery = query.toLowerCase();
      return actions
        .filter((action) => {
          if (action.disabled) return false;
          const matchTitle = action.title.toLowerCase().includes(lowerQuery);
          const matchDescription = action.description?.toLowerCase().includes(lowerQuery);
          const matchKeywords = action.keywords?.some((k) => k.toLowerCase().includes(lowerQuery));
          return matchTitle || matchDescription || matchKeywords;
        })
        .sort((a, b) => {
          // Prioritize title matches
          const aTitle = a.title.toLowerCase().startsWith(lowerQuery);
          const bTitle = b.title.toLowerCase().startsWith(lowerQuery);
          if (aTitle && !bTitle) return -1;
          if (!aTitle && bTitle) return 1;
          return 0;
        });
    }, [query, actions, recentActions]);

    // Group actions
    const groupedActions = useMemo(() => {
      const groups: Record<string, CommandAction[]> = {};
      filteredActions.forEach((action) => {
        const group = action.group || 'Actions';
        if (!groups[group]) groups[group] = [];
        groups[group].push(action);
      });
      return groups;
    }, [filteredActions]);

    // Reset selection when query changes
    useEffect(() => {
      setSelectedIndex(0);
    }, [query]);

    // Reset state when closed
    useEffect(() => {
      if (!opened) {
        setQuery('');
        setSelectedIndex(0);
      }
    }, [opened]);

    const handleSelect = useCallback(
      (action: CommandAction) => {
        onRecentAction?.(action.id);
        action.onTrigger();
        onClose();
      },
      [onClose, onRecentAction]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const total = filteredActions.length;

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % total);
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + total) % total);
            break;
          case 'Enter':
            e.preventDefault();
            if (filteredActions[selectedIndex]) {
              handleSelect(filteredActions[selectedIndex]);
            }
            break;
          case 'Escape':
            e.preventDefault();
            onClose();
            break;
        }
      },
      [filteredActions, selectedIndex, handleSelect, onClose]
    );

    let currentIndex = -1;

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        padding={0}
        size="lg"
        centered
        styles={{
          content: { overflow: 'hidden' },
        }}
      >
        <Box ref={ref}>
          {/* Search input */}
          <Box p="md" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
            <TextInput
              placeholder={searchPlaceholder || placeholder}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              leftSection={<IconSearch size={18} />}
              rightSection={
                showShortcuts && (
                  <Group gap={4}>
                    <Kbd size="xs">esc</Kbd>
                  </Group>
                )
              }
              autoFocus
              variant="unstyled"
              size="lg"
              styles={{
                input: { fontSize: theme.fontSizes.lg },
              }}
            />
          </Box>

          {/* Results */}
          <ScrollArea h={maxHeight} type="auto">
            {filteredActions.length === 0 ? (
              <Box p="xl" ta="center">
                <Text c="dimmed" style={accessibilityStyles}>{emptyMessage}</Text>
              </Box>
            ) : (
              Object.entries(groupedActions).map(([group, groupActions]) => (
                <Box key={group}>
                  <Text size="xs" fw={600} c="dimmed" px="md" py="xs" tt="uppercase" style={accessibilityStyles}>
                    {group}
                  </Text>
                  {groupActions.map((action) => {
                    currentIndex++;
                    const isSelected = currentIndex === selectedIndex;
                    const index = currentIndex;

                    return (
                      <Box
                        key={action.id}
                        px="md"
                        py="sm"
                        style={{
                          backgroundColor: isSelected ? `${gradient?.from}15` : undefined,
                          cursor: 'pointer',
                          transition: 'background-color 0.1s',
                        }}
                        onClick={() => handleSelect(action)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <Group justify="space-between">
                          <Group gap="sm">
                            {action.icon && (
                              <Box
                                style={{
                                  color: isSelected ? gradient?.from : theme.colors.gray[6],
                                }}
                              >
                                {action.icon}
                              </Box>
                            )}
                            <Box>
                              <Text size="sm" fw={500} style={accessibilityStyles}>
                                {action.title}
                              </Text>
                              {action.description && (
                                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                                  {action.description}
                                </Text>
                              )}
                            </Box>
                          </Group>

                          {showShortcuts && action.shortcut && (
                            <Group gap={4}>
                              {action.shortcut.map((key, i) => (
                                <Kbd key={i} size="xs">
                                  {key}
                                </Kbd>
                              ))}
                            </Group>
                          )}
                        </Group>
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </ScrollArea>

          {/* Footer hints */}
          <Box
            p="xs"
            style={{
              borderTop: `1px solid ${theme.colors.gray[2]}`,
              backgroundColor: theme.colors.gray[0],
            }}
          >
            <Group justify="center" gap="md">
              <Group gap={4}>
                <Kbd size="xs">↑↓</Kbd>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Navigate
                </Text>
              </Group>
              <Group gap={4}>
                <Kbd size="xs">↵</Kbd>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Select
                </Text>
              </Group>
              <Group gap={4}>
                <Kbd size="xs">esc</Kbd>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Close
                </Text>
              </Group>
            </Group>
          </Box>
        </Box>
      </Modal>
    );
  }
);

CommandPalette.displayName = 'CommandPalette';

export default CommandPalette;
